import { cn } from "~/lib/utils";

interface PageHeaderProps {
  title: string;
  /** Optional supporting copy shown under the title. */
  description?: string;
  /** Right-aligned actions (buttons). Sit top-right on the same row as the title. */
  actions?: React.ReactNode;
  /** Optional back control rendered top-left, above the title. */
  back?: React.ReactNode;
  className?: string;
}

/**
 * Standardized page header: an optional back control on top, then the title +
 * description on the left with the primary actions pinned top-right (same row
 * as the title on every breakpoint). The title wraps and the actions keep their
 * natural width, so they never overlap on narrow screens.
 */
export function PageHeader({
  title,
  description,
  actions,
  back,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("mb-6 space-y-3", className)}>
      {back}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            {title}
          </h1>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end [&>*]:flex-1 sm:[&>*]:flex-none">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
